import { AdvisorServicesAPI, computed, debug, NetbankingAPI } from "aaa";
import { flatMap } from "lodash";
import { action, observable } from "mobx";
import { AdvisorServicesAPIClient, EcosAPIClient } from "network/APIClient";
import { AccountsStore } from "stores/AccountsStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { OffersStore } from "stores/OffersStore";
import profileStore from "stores/ProfileStore";
import trackingStore from "stores/TrackingStore";
import { ISavingsAccount } from "types/account";
import { IOffer, IOfferConditionsResponse } from "types/offer";
import { ISigningInfo } from "types/signing";

const preferredTopics = ["SPAVO", "VERAN", "ABER"];
export const KAPITALSCHANGE = "KAPITALSCHANGE";
export const SMARTSCHANGE = "SMARTSCHANGE";
const trackableFlags = [
    "offerCapitalSavings",
    "offerSmartSavings",
    "advisorAppointment",
    "proposeInstituteChange",
];

// const terminationDateMapping = {
//     "terminatedByMidNovember": new Date("2025-11-17"),
//     "terminatedByMidJanuary": new Date("2026-01-20"),
//     "terminatedByMidMarch": new Date("2026-03-24")
// };
export class SavingsOptionsStore {
    @observable accountsStore?: AccountsStore;
    @observable offersStore: OffersStore;
    @observable _isLoading = true;
    @observable savingsAccount?: ISavingsAccount;

    @observable savingsOffer?: IOffer;

    @observable smartSavingsOffer?: IOffer;
    @observable capitalSavingsOffer?: IOffer;

    @observable canChooseCapitalSavings = false;
    @observable canChooseSmartSavings = false;
    @observable canBookAppointment = false;
    @observable proposeInstituteChange = false;

    // Termination flags
    // @observable terminatedByMidNovember = false;
    // @observable terminatedByMidJanuary = false;
    // @observable terminatedByMidMarch = false;

    @observable selectedOption?: "capitalSaving" | "smartSaving" | "appointment" | "instituteChange";
    @observable conditionsGroupCode?: "KAPITALSCHANGE" | "SMARTSCHANGE";

    @observable signInfo?: ISigningInfo;

    @observable smartSavingsInterestRate?: string;
    @observable capitalSavingsInterestRate?: string;
    @observable capitalSavingsDuration?: number;

    constructor() {
        // Initialize OffersStore with empty channelProducts, as it will be loaded later
        this.offersStore = new OffersStore([KAPITALSCHANGE, SMARTSCHANGE]);
        this.accountsStore = new AccountsStore();
    }

    @computed get isLoading() {
        return this.offersStore.isLoading;
    }

    // Product migration (Zinsgleitklausel / interest rate adjustment clause) journey only:
    // ignore account `terminatedBy*` flags for consistent Tranche 3 messaging; hard-coded until
    // business confirms future use of terminatedBy for other cases.
    terminationDate: Date = new Date(2026, 5, 23);
    // @computed get terminationDate(): Date | null {
    //     if (this.terminatedByMidNovember) return terminationDateMapping["terminatedByMidNovember"];
    //     if (this.terminatedByMidJanuary) return terminationDateMapping["terminatedByMidJanuary"];
    //     if (this.terminatedByMidMarch) return terminationDateMapping["terminatedByMidMarch"];
    //     return null;
    // }



    @action async loadData() {
        try {
            this._isLoading = true;

            const savingAccountId = externalInterfaceStore.accountId;
            if (!savingAccountId) {
                throw new Error("Saving account not found");
            }

            await this.accountsStore?.load();
            const account = this.accountsStore?.getAccountById(savingAccountId);
            if (!account) {
                throw new Error("Savings account not found");
            }

            this.savingsAccount = account;
            this.canChooseCapitalSavings = !!account?.flags?.some(flag => flag === "offerCapitalSavings");
            this.canChooseSmartSavings = !!account?.flags?.some(flag => flag === "offerSmartSavings");
            this.canBookAppointment = !!account?.flags?.some(flag => flag === "advisorAppointment");
            this.proposeInstituteChange = !!account?.flags?.some(flag => flag === "proposeInstituteChange");
            // this.updateTerminationFlags(account?.flags || []);


            if (this.canChooseCapitalSavings || this.canChooseSmartSavings) {
                await this.offersStore.load();

                const savingOffers = this.offersStore.offers;
                if (!savingOffers) {
                    throw new Error("Savings offers not found");
                }

                // Doublecheck Savings Offers
                // Find the savings offer based on the flags
                const offerCapital = savingOffers.find(offer => offer.channelProduct.includes(KAPITALSCHANGE));
                if (!offerCapital) {
                    this.canChooseCapitalSavings = false;
                } else {
                    this.capitalSavingsOffer = offerCapital;
                    // Extract duration from marketingName when offerCapital is true
                    this.capitalSavingsDuration = this.extractDurationFromMarketingName(offerCapital.marketingName);
                }

                const offerSmart = savingOffers.find(offer => offer.channelProduct.includes(SMARTSCHANGE));
                if (!offerSmart) {
                    this.canChooseSmartSavings = false;
                } else {
                    this.smartSavingsOffer = offerSmart;
                }

                // Load the savings offer details only for enabled options
                for (const offer of savingOffers) {

                    // channelProduct: "SMARTSCHANGE"
                    // conditionsGroupCode: "SMARTAKT06"
                    // conditionsGroupId: "E12B44CFE49D7CB9"
                    // productId: "DC7943A244739C39"
                    // handover: {
                    //     url: "https://store.fat3.sparkasse.at/store/home?institute=198&productCode=G-KAPITALU-K&channel=09&conditionGroup=KAPU024MAK02&language=AT"
                    // }
                    // https://call.latest.myoffers.microf.cs.eb.lan.at/moc/rest/my/offers/savings/conditions/G-SMART-K/SMARTAKT01/188

                    //http://localhost:1337/https://ecos.f3.erstegroup.com/rest/my/offers/savings/conditions/G-KAPITALU-K/KAPU024MAK02/188

                    // Check if this offer type is enabled for the user
                    const isSmartSavingsOffer = offer.channelProduct.includes(SMARTSCHANGE);
                    const isCapitalSavingsOffer = offer.channelProduct.includes(KAPITALSCHANGE);

                    const shouldFetchConditions = (isSmartSavingsOffer && this.canChooseSmartSavings) ||
                        (isCapitalSavingsOffer && this.canChooseCapitalSavings);

                    if (!shouldFetchConditions) {
                        continue;
                    }

                    let offerConditionHandoverURL = new URL(offer.handover?.url || "");
                    const productCode = offerConditionHandoverURL.searchParams.get('productCode');
                    const instituteId = profileStore.profile?.instituteId;

                    if (!productCode) {
                        debug.log("[SavingsOptionsStore] loadData error: productCode is not set");
                        continue;
                    }

                    if (!instituteId) {
                        debug.log("[SavingsOptionsStore] loadData error: instituteId is not set");
                        continue;
                    }

                    const offerConditionsTargetNetbanking = NetbankingAPI.getSavingsOfferConditions(productCode, offer.conditionsGroupCode, instituteId);
                    const offerConditionsData = await EcosAPIClient.requestType<IOfferConditionsResponse>(offerConditionsTargetNetbanking);

                    // Extract interest rate from the response
                    const interestRate = this.extractInterestRate(offerConditionsData);

                    // Differentiate between smart savings and capital savings offers
                    if (isSmartSavingsOffer) {
                        this.smartSavingsInterestRate = interestRate;
                    } else if (isCapitalSavingsOffer) {
                        this.capitalSavingsInterestRate = interestRate;
                    }
                }

            }

            // track event for savings options overview
            let eventLabel = this.savingsAccount?.flags?.sort().reduce((acc, flag) => {
                if (trackableFlags.includes(flag)) {
                    return acc === "" ? flag : `${acc}_${flag}`;
                }
                return acc;
            }, "");

            await trackingStore.trackEvent({ eventName: "savingsOptionsOverview", productKey: eventLabel });

            if (this.canBookAppointment) {
                const topicsTarget = AdvisorServicesAPI.getTopics();
                const topicsData = await AdvisorServicesAPIClient.requestType(topicsTarget);
                const topics = topicsData.topics;

                // get the first matching topic of preferredTopics and set it for appointment feature
                externalInterfaceStore.topicId = flatMap(preferredTopics, validTopic => topics.find(topic => topic.id === validTopic))?.[0]?.id;
            }

            this._isLoading = false;
        } catch (error) {
            debug.log("[SavingsOptionsStore] loadData error:", error);
            throw error;
        } finally {
            this._isLoading = false;
        }
    }


    // @action updateTerminationFlags(flags: string[]) {
    //     this.terminatedByMidNovember = flags.includes("terminatedByMidNovember");
    //     this.terminatedByMidJanuary = flags.includes("terminatedByMidJanuary");
    //     this.terminatedByMidMarch = flags.includes("terminatedByMidMarch");
    // }

    /**
     * Extracts the interest rate from the offer conditions response
     * @param offerConditionsData - The offer conditions response data
     * @returns The interest rate as a formatted string, or undefined if not found
     */
    private extractInterestRate(offerConditionsData: IOfferConditionsResponse): string | undefined {
        try {
            const chargingInfo = offerConditionsData?.chargingInformation;
            if (!chargingInfo?.kondiResponse) {
                debug.log("[SavingsOptionsStore] extractInterestRate: No kondiResponse found");
                return undefined;
            }

            // Find the HABENZINSEN condition (interest rate)
            const interestCondition = chargingInfo.kondiResponse.find(
                condition => condition.krzKondardStamm === "HABENZINSEN"
            );

            if (!interestCondition) {
                debug.log("[SavingsOptionsStore] extractInterestRate: No HABENZINSEN condition found");
                return undefined;
            }

            return interestCondition.stzKondition.toString();
        } catch (error) {
            debug.log("[SavingsOptionsStore] extractInterestRate error:", error);
            return undefined;
        }
    }

    /**
     * Extracts the duration (first number) from the marketingName
     * @param marketingName - The marketing name string (e.g., "s Kapital Sparen 24 MO AK")
     * @returns The duration as a number, or undefined if not found
     */
    private extractDurationFromMarketingName(marketingName?: string): number | undefined {
        if (!marketingName) {
            return undefined;
        }

        try {
            // Find the first number in the marketingName
            const match = marketingName.match(/\d+/);
            if (match) {
                return parseInt(match[0], 10);
            }
            return undefined;
        } catch (error) {
            debug.log("[SavingsOptionsStore] extractDurationFromMarketingName error:", error);
            return undefined;
        }
    }

}



// WEBPACK FOOTER //
// ./src/components/savingsOptions/SavingsOptionsStore.tsx